import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ID } from '@datorama/akita';
import { finalize } from 'rxjs/operators';

import { ApplicationService } from '@state/application';
import { EntitiesService, Entity } from '@state/entities';
import { EntityCard, EntityCardBadge } from '@component/entity-card';
import { HierarchyNode } from '@component/hierarchy';
import { titleize } from '@helpers/string';
import { FormWizardQuery } from '@state/form-wizard';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-entity-selector',
  styleUrls: ['entity-selector.component.scss'],
  templateUrl: 'entity-selector.component.html'
})
export class EntitySelectorComponent implements OnInit {
  @Input() entities: Entity[];
  @Input() showDraftStatus = false;
  @Input() vehicle: { id: ID; entity_id: ID };
  @Output() entitySelected: EventEmitter<Entity> = new EventEmitter();

  isSubmitting$ = this.formWizardQuery.isSubmitting$;
  nodes: HierarchyNode[] = [];

  constructor(
    private applicationService: ApplicationService,
    private formWizardQuery: FormWizardQuery,
    private entitiesService: EntitiesService
  ) {}

  ngOnInit() {
    this.createNodes(this.entities);
  }

  selectEntity = (entity: Entity): void => {
    this.entitySelected.emit(entity);
  };

  isSelf(entity: Entity): boolean {
    return entity.id === this.vehicle?.entity_id;
  }

  createCard({ active, entity }: { active?: boolean; entity: Entity }): EntityCard {
    let { selectEntity: action, showDraftStatus } = this;
    let primaryBadge: EntityCardBadge;

    if (entity.draft && showDraftStatus) {
      primaryBadge = {
        label: 'Draft',
        tooltip: 'This entity has not yet been finalized'
      };
    }

    return {
      active,
      data: [
        {
          label: 'Investments',
          value: entity?.investment_account?.total_investments
            ? `${entity?.investment_account?.total_investments}`
            : ''
        },
        {
          label: 'Total Invested',
          value: entity?.investment_account?.total_invested
        }
      ],
      meta: entity.type ? titleize(entity.type) : '–',
      name: entity.display_name ? entity.display_name : entity.legal_name,
      primaryAction: { action, data: entity, disabled: this.isSelf(entity), label: 'Select' },
      primaryBadge
    };
  }

  createNode({ active, entity }: { active?: boolean; entity: Entity }): HierarchyNode {
    return { card: this.createCard({ active, entity }), entity_id: entity.id };
  }

  createNodes(entities: Entity[]): void {
    for (let entity of entities) {
      this.nodes.push(this.createNode({ active: false, entity }));
    }
  }

  toggleChildren(node: HierarchyNode): void {
    if (node.loading) return;
    if (node.children) {
      delete node.children;
    } else {
      node.loading = true;

      this.applicationService
        .load$(this.entitiesService.getChildren$(node.entity_id), 'entity-selector/children')
        .pipe(
          finalize(() => {
            node.loading = false;
          })
        )
        .subscribe({
          next: (entities) => {
            node.children = entities.map((entity) => this.createNode({ entity }));
          }
        });
    }
  }
}
